"use client";

import { useState } from "react";
import { Search, TrendingUp, ArrowUpDown, Zap, Target, Lock, ChevronUp, ChevronDown } from "lucide-react";

interface KeywordResult {
  keyword: string;
  kd: number;
  kd_level: string;
  volume: string;
  raw_count?: number;
}

interface FreeKeywordClientProps {
  currentUser?: any;
  subscription?: any;
}

const FreeKeywordClient: React.FC<FreeKeywordClientProps> = ({
  currentUser,
  subscription,
}) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<KeywordResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<'kd' | 'volume' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [hasSearched, setHasSearched] = useState(false);

  const FREE_LIMIT = 10; // Usuarios free solo ven 10 resultados
  const isSubscribed = subscription?.status === 'active' || currentUser?.role === 'admin';

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setSortBy(null);
    setHasSearched(true);
    try {
      const res = await fetch(`/api/keywords?query=${encodeURIComponent(query)}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to fetch keywords');
      }

      setResults(data.suggestions);
      setTotal(data.total || data.suggestions.length);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch keyword data");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: 'kd' | 'volume') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getVolumeNumeric = (volume: string): number => {
    if (volume === ">5k") return 5001;
    if (volume === "1k-5k") return 3000;
    if (volume === "500-1k") return 750;
    if (volume === "100-500") return 300;
    if (volume === "<100") return 50;
    return 0;
  };

  const getSortedResults = () => {
    if (!sortBy) return results;

    const sorted = [...results].sort((a, b) => {
      let compareValue = 0;

      if (sortBy === 'kd') {
        compareValue = a.kd - b.kd;
      } else if (sortBy === 'volume') {
        compareValue = getVolumeNumeric(a.volume) - getVolumeNumeric(b.volume);
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return sorted;
  };

  const sortedResults = getSortedResults();
  const displayedResults = isSubscribed ? sortedResults : sortedResults.slice(0, FREE_LIMIT);
  const hasMoreResults = !isSubscribed && sortedResults.length > FREE_LIMIT;

  const getKDColor = (level: string) => {
    switch (level) {
      case "Easy":
        return "text-emerald-600 bg-emerald-50";
      case "Medium":
        return "text-amber-600 bg-amber-50";
      case "Hard":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getVolumeColor = (volume: string) => {
    if (volume === ">5k" || volume === "1k-5k") return "text-blue-700";
    if (volume === "500-1k") return "text-blue-600";
    if (volume === "100-500") return "text-gray-700";
    return "text-gray-600";
  };

  const SortIcon = ({ column }: { column: 'kd' | 'volume' }) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400" />;
    }
    return sortOrder === 'asc' ?
      <ChevronUp className="w-3 h-3 text-emerald-600" /> :
      <ChevronDown className="w-3 h-3 text-emerald-600" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full sm:text-sm text-xs font-medium mb-4">
            <Zap className="w-4 h-4" />
            100% Free Tool
          </div>
          <h1 className="sm:text-5xl text-3xl font-bold text-gray-900 mb-4 tracking-tight">
            Free SEO Keyword Generator
          </h1>
          <p className="sm:text-xl text-lg text-gray-600">
            Discover long-tail keywords you can rank for and boost your organic traffic
          </p>
        </div>

        {/* Search Section */}
        <div className="bg-white border-2 border-gray-200 rounded-2xl shadow-lg p-6 mb-10 hover:shadow-xl transition-shadow">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder='Enter seed keyword (e.g., "scuba instructor")'
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none text-md transition-all"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              className="px-8 py-4 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg shadow-emerald-600/20 hover:shadow-xl hover:shadow-emerald-600/30 whitespace-nowrap"
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
          <p className="text-xs text-gray-700 mt-2">*More specific searches give better results.</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-8">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && (
          <div className="bg-white border-2 border-gray-200 rounded-2xl shadow-xl overflow-hidden mb-8">
            {/* Results Header */}
            <div className="border-b-2 border-gray-200 px-6 py-5 bg-gradient-to-br from-white to-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Long-Tail Keyword ideas for "{query}"
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {/* {isSubscribed ? (
                      `Showing all ${total} keywords`
                    ) : (
                      <>Showing {Math.min(FREE_LIMIT, total)} of {total} keywords • <span className="text-emerald-600 font-semibold">Upgrade to see all</span></>
                    )} */}
                    Showing {total} keywords
                  </p>
                </div>
              </div>
            </div>

            {/* Table Header - Desktop only */}
            <div className="hidden md:block border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-12 gap-4 px-6 py-3 text-xs font-bold text-gray-700 uppercase tracking-wide">
                <div className="col-span-6">Keyword</div>
                <div
                  className="ml-3 col-span-2 flex items-center gap-1 cursor-pointer hover:text-emerald-600 transition-colors"
                  onClick={() => handleSort('kd')}
                >
                  KD
                  <SortIcon column="kd" />
                </div>
                <div
                  className="col-span-2 flex items-center gap-1 cursor-pointer hover:text-emerald-600 transition-colors"
                  onClick={() => handleSort('volume')}
                >
                  Volume
                  <SortIcon column="volume" />
                </div>
                <div className="col-span-2 text-right">Updated</div>
              </div>
            </div>

            {/* Mobile Sort Controls */}
            <div className="md:hidden border-b border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex items-center justify-between text-xs">
                <button
                  onClick={() => handleSort('kd')}
                  className="flex items-center gap-1 font-semibold text-gray-700 hover:text-emerald-600 transition-colors"
                >
                  Sort by KD
                  <SortIcon column="kd" />
                </button>
                <button
                  onClick={() => handleSort('volume')}
                  className="flex items-center gap-1 font-semibold text-gray-700 hover:text-emerald-600 transition-colors"
                >
                  Sort by Volume
                  <SortIcon column="volume" />
                </button>
              </div>
            </div>
            {/* Results List */}
            <div className="divide-y divide-gray-100">
              {displayedResults.map((item, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-4 sm:px-6 px-2 sm:py-4 py-2 hover:bg-gray-50 transition-colors items-center"
                >
                  {/* Keyword */}
                  <div className="sm:col-span-6 col-span-5">
                    <span className="text-gray-900 font-medium sm:text-md text-sm">
                      {item.keyword}
                    </span>
                  </div>

                  {/* KD */}
                  {/* <div className="col-span-2">
                    <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold ${getKDColor(item.kd_level)}`}>
                      {item.kd_level}
                    </span>
                  </div> */}
                  <div className="col-span-2">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold ${getKDColor(item.kd_level)}`}
                    >
                      {/* En móviles solo acorta Medium */}
                      <span className="sm:hidden">
                        {item.kd_level === "Medium" ? "Med" : item.kd_level}
                      </span>

                      {/* En pantallas medianas o mayores muestra el texto completo */}
                      <span className="hidden sm:inline">{item.kd_level}</span>
                    </span>
                  </div>


                  {/* Volume */}
                  <div className="sm:col-span-2 col-span-3">
                    <span className={`font-bold text-sm ${getVolumeColor(item.volume)}`}>
                      {item.volume}
                    </span>
                  </div>

                  {/* Updated */}
                  <div className="sm:col-span-2 col-span-1 text-right sm:text-sm text-xs text-gray-600">
                    Today
                  </div>
                </div>
              ))}
            </div>

            <div className="blur-sm pointer-events-none divide-y divide-gray-100">
              {/* {sortedResults.slice(FREE_LIMIT, FREE_LIMIT + 5).map((item, idx) => ( */}
              <div
                // key={idx}
                className="grid grid-cols-12 gap-4 sm:px-6 px-2 sm:py-4 py-2 hover:bg-gray-50 transition-colors items-center"
              >
                <div className="sm:col-span-6 col-span-5">
                  <span className="text-gray-900 font-medium sm:text-md text-sm">
                    hola capybarita anselmo
                  </span>
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold text-emerald-600 bg-emerald-50`}>
                    Easy
                  </span>
                </div>
                <div className="sm:col-span-2 col-span-3">
                  <span className={`font-bold text-sm text-blue-700`}>
                    1k-5k
                  </span>
                </div>
                <div className="sm:col-span-2 col-span-1 text-right sm:text-sm text-xs text-gray-600">
                  Today
                </div>
              </div>
              {/* ))} */}
              <div
                // key={idx}
                className="grid grid-cols-12 gap-4 sm:px-6 px-2 sm:py-4 py-2 hover:bg-gray-50 transition-colors items-center"
              >
                <div className="sm:col-span-6 col-span-5">
                  <span className="text-gray-900 font-medium sm:text-md text-sm">
                    hola capybarita anselmo
                  </span>
                </div>
                <div className="col-span-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold text-amber-600 bg-amber-50`}>
                    Medium
                  </span>
                </div>
                <div className="sm:col-span-2 col-span-3">
                  <span className={`font-bold text-sm text-blue-700`}>
                    1k-5k
                  </span>
                </div>
                <div className="sm:col-span-2 col-span-1 text-right sm:text-sm text-xs text-gray-600">
                  Today
                </div>
              </div>
            </div>


            {/* Locked Results Section */}
            <div className="relative">
              <div className="flex-row absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white z-20 flex items-center justify-center">
                {/* <div className="text-md text-gray-600 text-center">
                  Unlock all keyword suggestions, advanced filtering, and export features
                </div> */}
                <div className="mb-20">
                  {isSubscribed ? (
                    <a
                      href={`/dashboard/${currentUser.id}`}
                      className="bg-emerald-600 text-white px-8 py-4 rounded-xl hover:from-emerald-700 hover:to-emerald-800 transition-all font-bold text-lg shadow-lg hover:shadow-xl inline-block transform hover:scale-105"
                    >
                      See more at dashboard →
                    </a>
                  ) : (
                    <a
                      href="/register"
                      className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-8 py-4 rounded-xl hover:from-emerald-700 hover:to-emerald-800 transition-all font-bold text-lg shadow-lg hover:shadow-xl inline-block transform hover:scale-105"
                    >
                      Unlock All Results →
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Info */}
            <div className="border-t-2 border-gray-200 px-6 py-4 bg-gradient-to-br from-gray-50 to-white">
              <p className="text-xs text-gray-600">
                💡 <strong>KD</strong> = Keyword Difficulty (how hard to rank) • <strong>Volume</strong> = Estimated monthly searches
              </p>
            </div>
          </div>
        )}

        {/* Empty State - Initial */}
        {!loading && results.length === 0 && !hasSearched && (
          <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl shadow-lg sm:p-16 p-10 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Target className="w-10 h-10 text-white" />
            </div>
            <h3 className="sm:text-2xl text-xl font-bold text-gray-900 mb-3">
              Start Your Long-Tail Keyword Research
            </h3>
            <p className="text-gray-600 sm:text-lg text-md max-w-md mx-auto mb-8">
              Explore free long-tail keywords with difficulty & search volume insights
            </p>

            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 bg-emerald-500 rounded-md shadow-sm"></span>
                <span className="text-gray-700 font-medium">Easy to rank</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 bg-amber-500 rounded-md shadow-sm"></span>
                <span className="text-gray-700 font-medium">Medium difficulty</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 bg-red-500 rounded-md shadow-sm"></span>
                <span className="text-gray-700 font-medium">Hard to rank</span>
              </div>
            </div>

            <p className="text-gray-600 sm:text-sm text-xs mt-8">
              Data sourced from Bing, the AI-powered, up-to-date search engine.
            </p>
          </div>
        )}

        {/* Empty State - No Results (solo después de búsqueda fallida) */}
        {hasSearched && results.length === 0 && !loading && (
          <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl shadow-lg p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              No keywords found
            </h3>
            <p className="text-gray-600">
              Try a different keyword or check your spelling
            </p>
          </div>
        )}

        {/* CTA Section */}
        {!currentUser?.hasSubscription && results.length > 0 && (
          <div className="mt-16 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl shadow-2xl sm:p-12 p-8 text-center border-2 border-emerald-500 relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>
            <div className="relative z-10">
              <h2 className="sm:text-4xl text-3xl font-bold text-white mb-4">
                Turn Keywords into Free Traffic — Automatically
              </h2>
              <p className="sm:text-xl text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
                Start creating optimized articles fast and automatically.
                Join thousands of startups ranking on Google, Bing, ChatGPT and Perplexity with AI-powered content tools.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/register"
                  className="bg-white text-emerald-700 px-10 py-5 rounded-xl hover:bg-emerald-50 transition-all font-bold text-lg shadow-2xl hover:shadow-emerald-900/30 hover:scale-105 transform inline-block"
                >
                  Start Free Trial →
                </a>
                <a
                  href="#"
                  className="bg-emerald-800 text-white px-10 py-5 rounded-xl hover:bg-emerald-900 transition-all font-semibold text-lg border-2 border-emerald-400/30 inline-block"
                >
                  View Pricing
                </a>
              </div>
              <p className="text-emerald-200 text-sm mt-6">
                ✨ No credit card required • 🚀 Set up in 2 minutes
              </p>
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default FreeKeywordClient;